import { t } from '@lingui/core/macro';
import type { StudentPlacementStatus } from '@duke-hq/svc-api-db';
import { StatusPill } from './StatusPill';

type StageMetadata = {
  label: string;
  colorScheme: string;
};

// -- Stage colour mapping -----------------------------------------------------
//
// The ten placement statuses collapse into three semantic colour groups so the
// drawer reads at a glance rather than as a rainbow:
//   - review   (orange): waiting on an admin — awaiting docs / pending approval
//   - motion   (brand):  the placement is progressing — sourcing through evidence
//   - terminal (green / red / neutral): finished states, which carry no
//     attention colour of their own
export function getStageMetadata(): Record<StudentPlacementStatus, StageMetadata> {
  return {
    AWAITING_CHECKLIST: { label: t`Awaiting docs`, colorScheme: 'orange' },
    AWAITING_APPROVAL: { label: t`Pending approval`, colorScheme: 'orange' },
    AWAITING_PLACEMENT: { label: t`Sourcing`, colorScheme: 'brand' },
    INTERVIEWING: { label: t`Interviewing`, colorScheme: 'brand' },
    IN_PROGRESS: { label: t`In progress`, colorScheme: 'brand' },
    AWAITING_EVIDENCE: { label: t`Awaiting evidence`, colorScheme: 'brand' },
    COMPLETED: { label: t`Completed`, colorScheme: 'green' },
    FAILED: { label: t`Failed`, colorScheme: 'red' },
    CANCELLED: { label: t`Cancelled`, colorScheme: 'gray' },
  };
}

export function StageBadge({
  status,
  onHold = false,
  'data-testid': dataTestId,
}: {
  status: StudentPlacementStatus;
  onHold?: boolean;
  'data-testid'?: string;
}): React.ReactElement {
  // On hold takes precedence over the underlying placement status.
  const { label, colorScheme } = onHold
    ? { label: t`On hold`, colorScheme: 'gray' }
    : getStageMetadata()[status];
  return <StatusPill label={label} colorScheme={colorScheme} data-testid={dataTestId} />;
}
